import type { AgentBillingAddCreditsModalProps } from "@v2/types/modalTypes";
import type { AgentBillingPlan } from "schemas/agent-billing";
import type { AppModalType } from "./AppModalTypes";

import * as React from "react";

import { DeleteComponentTemplateConfirmationModal } from "@v1/components/editor/page/element-editor/components/DeleteComponentTemplateConfirmationModal";
import { DeleteElementConfirmationModal } from "@v1/components/editor/page/element-editor/components/DeleteElementConfirmationModal";
import { DeleteSwatchConfirmationModal } from "@v1/components/editor/page/element-editor/components/DeleteSwatchConfirmationModal";
import { FullPageErrorModal } from "@v1/components/editor/page/FullPageErrorModal";
import { ProjectMembershipModal } from "@v1/components/editor/page/ProjectMembershipModal";
import { StorePasswordRequiredModal } from "@v1/components/editor/page/StorePasswordRequiredModal";
import { SupportModal } from "@v1/components/editor/page/SupportModal";
import { WorkspaceMembershipModal } from "@v1/components/editor/page/WorkspaceMembershipModal";
import { ExportToSectionModal } from "@v1/components/ExportToSectionModal";
import { HotkeysModal } from "@v1/components/HotkeysModal";
import { CodeEditorModal } from "@v1/components/modals/CodeEditorModal";
import { PermanentlyDeleteElementModal } from "@v1/components/modals/PermanentlyDeleteElementModal";
import { RemoveShopifyIntegrationModal } from "@v1/components/modals/RemoveShopifyIntegrationModal";
import { SaveTemplateModal } from "@v1/components/templateLibrary/SaveTemplateModal";
import { FunnelAnalyticsModal } from "@v1/features/analytics/modals/FunnelAnalyticsModal";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import AgentBillingConfettiOverlay from "@v2/features/agent-billing/AgentBillingConfettiOverlay";
import { AgentBillingAddCreditsModal } from "@v2/features/agent-billing/components/AgentBillingAddCreditsModal";
import { useRateCards } from "@v2/features/agent-billing/hooks/useRateCards";
import AgentBillingAddPublishedElementsModal from "@v2/features/agent-billing/modals/AgentBillingAddPublishedElementsModal";
import AgentBillingAddShopifyThemeBuilderProjectsModal from "@v2/features/agent-billing/modals/AgentBillingAddShopifyThemeBuilderProjectsModal";
import AgentBillingAddSitesModal from "@v2/features/agent-billing/modals/AgentBillingAddSitesModal";
import { PlanChangeConfirmationModal } from "@v2/features/agent-billing/modals/PlanChangeConfirmationModal";
import { rateCard as rateCardUtils } from "@v2/features/agent-billing/utils/rateCard";
import {
  ADDON_APPROVAL_CONFIRMATION_PENDING_KEY,
  showAddonApprovalConfirmationToast,
  showSubscriptionUpdateSuccessToast,
} from "@v2/features/billing/billingToasts";
import { BillingErrorModal } from "@v2/features/billing/modals/BillingErrorModal";
import { BillingStripeNotificationModal } from "@v2/features/billing/modals/BillingStripeNotificationModal";
import PublishedElementsEntitlementRejectedModal from "@v2/features/billing/modals/PublishedElementsEntitlementRejectedModal";
import ReploSiteEntitlementRejectedModal from "@v2/features/billing/modals/ReploSiteEntitlementRejectedModal";
import ShopifyThemeProjectEntitlementRejectedModal from "@v2/features/billing/modals/ShopifyThemeProjectEntitlementRejectedModal";
import { SelectProjectForTemplateModal } from "@v2/features/share-page-template/SelectProjectForTemplateModal";
import { useModal, useModalActions } from "@v2/store/modalStore";
import { useSearchParams } from "react-router-dom";
import { BILLING_PLAN_CONFIRMATION_PARAM } from "schemas/billing";

const AgentBillingUpgradeModalWrapper: React.FC<{
  plan?: AgentBillingPlan;
}> = ({ plan }) => {
  const { closeModal } = useModalActions();
  const { rateCards } = useRateCards();

  const upgradePlan = plan ?? "starter";
  const upgradeRateCard = rateCards?.find(
    (rateCard) => rateCard.plan === upgradePlan,
  );
  const planPriceId = upgradeRateCard
    ? rateCardUtils.getDefaultPriceId({
        rateCard: upgradeRateCard,
        productType: upgradePlan,
        interval: "year",
      }) ?? ""
    : "";

  return (
    <PlanChangeConfirmationModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "agentBillingUpgradeModal" });
        }
      }}
      subscriptionView={{
        subscriptionItems: [
          {
            productType: upgradePlan,
            priceId: planPriceId,
            quantity: 1,
            interval: "year",
          },
        ],
      }}
    />
  );
};

const AgentBillingAddCreditsModalWrapper: React.FC<
  AgentBillingAddCreditsModalProps
> = ({ featureType }) => {
  const { closeModal } = useModalActions();
  return (
    <AgentBillingAddCreditsModal
      featureType={featureType}
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "agentBillingAddCreditsModal" });
        }
      }}
    />
  );
};

const modalMapping = {
  projectMembershipModal: { ModalComponent: ProjectMembershipModal },
  permanentlyDeleteElementModal: {
    ModalComponent: PermanentlyDeleteElementModal,
  },
  deleteComponentTemplateConfirmationModal: {
    ModalComponent: DeleteComponentTemplateConfirmationModal,
  },
  deleteElementConfirmationModal: {
    ModalComponent: DeleteElementConfirmationModal,
  },
  deleteSwatchConfirmationModal: {
    ModalComponent: DeleteSwatchConfirmationModal,
  },
  saveTemplateModal: { ModalComponent: SaveTemplateModal },
  hotkeysModal: { ModalComponent: HotkeysModal },
  supportModal: { ModalComponent: SupportModal },
  storePasswordRequiredModal: {
    ModalComponent: StorePasswordRequiredModal,
  },
  fullPageErrorModal: {
    ModalComponent: FullPageErrorModal,
  },
  billingErrorModal: {
    ModalComponent: BillingErrorModal,
  },
  billingStripeNotificationModal: {
    ModalComponent: BillingStripeNotificationModal,
  },
  shopifyThemeProjectEntitlementRejectedModal: {
    ModalComponent: ShopifyThemeProjectEntitlementRejectedModal,
  },
  publishedElementsEntitlementRejectedModal: {
    ModalComponent: PublishedElementsEntitlementRejectedModal,
  },
  reploSiteEntitlementRejectedModal: {
    ModalComponent: ReploSiteEntitlementRejectedModal,
  },
  exportToSectionModal: {
    ModalComponent: ExportToSectionModal,
  },
  removeShopifyIntegrationModal: {
    ModalComponent: RemoveShopifyIntegrationModal,
  },
  codeEditorModal: { ModalComponent: CodeEditorModal },
  workspaceMembershipModal: {
    ModalComponent: WorkspaceMembershipModal,
  },
  funnelAnalyticsModal: {
    ModalComponent: FunnelAnalyticsModal,
  },
  selectProjectForTemplateModal: {
    ModalComponent: SelectProjectForTemplateModal,
  },
  agentBillingAddPublishedElementsModal: {
    ModalComponent: AgentBillingAddPublishedElementsModal,
  },
  agentBillingAddSitesModal: {
    ModalComponent: AgentBillingAddSitesModal,
  },
  agentBillingAddShopifyThemeBuilderProjectsModal: {
    ModalComponent: AgentBillingAddShopifyThemeBuilderProjectsModal,
  },
  agentBillingAddCreditsModal: {
    ModalComponent: AgentBillingAddCreditsModalWrapper,
  },
  agentBillingUpgradeModal: {
    ModalComponent: AgentBillingUpgradeModalWrapper,
  },
} satisfies {
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  [ModalType in AppModalType]: { ModalComponent: React.ComponentType<any> };
};

const AppModals = () => {
  const { modals } = useModal();

  const [searchParams, setSearchParams] = useSearchParams();
  const errorToast = useV1ErrorToast();
  const billingConfirmParam = searchParams.get(BILLING_PLAN_CONFIRMATION_PARAM);

  // Handle successful plan update redirect from payment processor
  const isBillingPlanConfirmed = billingConfirmParam === "true";
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (isBillingPlanConfirmed) {
      const isAddonApprovalConfirmation =
        window.localStorage.getItem(ADDON_APPROVAL_CONFIRMATION_PENDING_KEY) ===
        "true";
      window.localStorage.removeItem(ADDON_APPROVAL_CONFIRMATION_PENDING_KEY);
      if (isAddonApprovalConfirmation) {
        showAddonApprovalConfirmationToast();
      } else {
        showSubscriptionUpdateSuccessToast();
      }
      setSearchParams(
        (params) => {
          params.delete(BILLING_PLAN_CONFIRMATION_PARAM);
          return new URLSearchParams(params);
        },
        { replace: true },
      );
    }
  }, [isBillingPlanConfirmed, setSearchParams]);

  // Handle failed plan update redirect from payment processor
  const isBillingPlanFailure = billingConfirmParam === "false";
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (isBillingPlanFailure) {
      window.localStorage.removeItem(ADDON_APPROVAL_CONFIRMATION_PENDING_KEY);
      errorToast(
        "Something went wrong updating your plan",
        "Please contact support@replo.app for help.",
        "error.billing.update.failed",
        {},
      );
      setSearchParams(
        (params) => {
          params.delete(BILLING_PLAN_CONFIRMATION_PARAM);
          return new URLSearchParams(params);
        },
        { replace: true },
      );
    }
  }, [isBillingPlanFailure, setSearchParams, errorToast]);

  const rtn = [];
  for (const [type, params] of Object.entries(modals || {})) {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const Modal = modalMapping[type as AppModalType].ModalComponent;
    if (!Modal) {
      continue;
    }
    rtn.push(<Modal key={type} {...params} />);
  }
  return (
    <>
      {rtn}
      <AgentBillingConfettiOverlay />
    </>
  );
};

export default AppModals;
